import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export function ChangePassword() {
  const navigate = useNavigate();
  const { passwordChanged } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) { setError('New passwords do not match'); return; }
    if (next.length < 12) { setError('Password must be at least 12 characters'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      passwordChanged();
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? (e.message ?? 'Failed to change password') : 'Network error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl text-ink">QRAM</h1>
        </div>
        <div className="bg-white border border-ink-200 rounded p-6 shadow-sm">
          <h2 className="text-lg font-display text-ink mb-1">Set your password</h2>
          <p className="text-sm text-ink-500 mb-5">
            Your account was created with a temporary password. Please set a permanent one before continuing.
          </p>
          {error && (
            <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block">
              <span className="text-sm text-ink-700">Current (temporary) password</span>
              <input type="password" required autoFocus autoComplete="current-password"
                value={current} onChange={(e) => setCurrent(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent" />
            </label>
            <label className="block">
              <span className="text-sm text-ink-700">New password (12+ characters)</span>
              <input type="password" required autoComplete="new-password"
                value={next} onChange={(e) => setNext(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent" />
            </label>
            <label className="block">
              <span className="text-sm text-ink-700">Confirm new password</span>
              <input type="password" required autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent" />
            </label>
            <button type="submit" disabled={submitting || !current || !next || !confirm}
              className="w-full bg-accent text-white py-2 rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 mt-2">
              {submitting ? 'Saving…' : 'Set password and continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}